/**
 * Practice-plan math, ported from templates/practice-planner.php
 * (calcDur / periodDurationValue / timeToMin / minToTime / getPlanStart /
 * applyStartTime / savePeriod). Pure functions over PlanData — no DOM.
 */
import type { HHMM, Period, PlanData } from '../types';

export const DEFAULT_PERIOD_MIN = 8;
export const DEFAULT_START: HHMM = '15:00';

/** "15:00:00" → "15:00". Tolerates undefined. */
export function fmt(t: string | undefined | null): HHMM {
  return t ? t.substring(0, 5) : '';
}

export function timeToMin(t: string | undefined | null): number {
  const [h, m] = (t || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function minToTime(m: number): HHMM {
  m = ((m % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/**
 * Duration of period i in minutes, or null. Every period except the last
 * derives its length from the NEXT period's start time; the last one uses
 * its stored `duration`.
 */
export function periodDuration(ps: Period[], i: number): number | null {
  const p = ps[i];
  if (!p) return null;
  if (i >= ps.length - 1) return p.duration ? p.duration : null;
  const d = timeToMin(ps[i + 1]!.time) - timeToMin(p.time);
  return d > 0 ? d : null;
}

/** Plugin's calcDur(): "12m" or "". */
export function calcDur(ps: Period[], i: number): string {
  const d = periodDuration(ps, i);
  return d ? `${d}m` : '';
}

/** Plugin's periodDurationValue(): falls back to 8. */
export function periodDurationValue(ps: Period[], i: number): number {
  return periodDuration(ps, i) ?? DEFAULT_PERIOD_MIN;
}

/** Effective start = first period's time, else stored startTime, else 15:00. */
export function getPlanStart(plan: PlanData): HHMM {
  const first = plan.periods[0];
  if (first && first.time) return fmt(first.time);
  return plan.startTime || DEFAULT_START;
}

/**
 * Shift every period by the delta between the current start and the new
 * start. Returns the new plan and the delta in minutes (0 when nothing moved).
 */
export function applyStartTime(plan: PlanData, newStart: HHMM): { plan: PlanData; delta: number } {
  if (!newStart) return { plan, delta: 0 };
  newStart = fmt(newStart);
  const oldStart = getPlanStart(plan);
  const delta = timeToMin(newStart) - timeToMin(oldStart);
  const next: PlanData = { ...plan, startTime: newStart };
  if (!delta || !plan.periods.length) return { plan: next, delta: 0 };
  next.periods = plan.periods.map((p) =>
    p.time ? { ...p, time: minToTime(timeToMin(p.time) + delta) } : p,
  );
  return { plan: next, delta };
}

export function emptyPlan(name = 'New Practice'): PlanData {
  return { name, offFocus: '', defFocus: '', startTime: DEFAULT_START, periods: [] };
}

export function sortPeriods(ps: Period[]): Period[] {
  return [...ps].sort((a, b) => a.time.localeCompare(b.time));
}

/**
 * Save the period editor. A new period (editingIdx null) goes on the end of
 * the practice; an existing one is edited in place via editPeriod(). Kept
 * under the plugin's name (savePeriod) for callers and tests.
 */
export function upsertPeriod(
  plan: PlanData,
  period: Period,
  editingIdx: number | null,
): PlanData {
  if (editingIdx === null || editingIdx < 0 || editingIdx >= plan.periods.length) return appendPeriod(plan, period);
  return editPeriod(plan, editingIdx, period).plan;
}

/**
 * Add a period after the last one. Its start is wherever the practice ends,
 * whatever the editor said — except on an empty plan, where the typed start
 * becomes the practice start.
 */
export function appendPeriod(plan: PlanData, period: Period): PlanData {
  const d = period.duration || DEFAULT_PERIOD_MIN;
  if (!plan.periods.length) {
    const start = fmt(period.time) || getPlanStart(plan);
    return { ...plan, startTime: start, periods: [{ ...period, time: start, duration: d }] };
  }
  return insertPeriod(plan, { ...period, duration: d }, plan.periods.length);
}

/**
 * Earliest start the editor may give period `idx`: one minute after the
 * period before it begins (that period can't shrink below 1 minute).
 * Null for the first period, which can start whenever it likes.
 */
export function earliestStart(plan: PlanData, idx: number): HHMM | null {
  if (idx <= 0 || idx > plan.periods.length) return null;
  return minToTime(timeToMin(plan.periods[idx - 1]!.time) + 1);
}

/**
 * Edit period `idx` in place. The period never changes its spot in the list,
 * and the schedule stays end to end:
 *  - new Minutes → everything after slides by the difference;
 *  - new Start   → this period and everything after move by the difference
 *    (lengths kept); the period before stretches or shrinks to meet it.
 *    On the first period that is the same as moving the whole practice.
 * A start at or before the previous period's start is refused (error set,
 * plan returned unchanged) rather than re-sorting periods out of order.
 */
export function editPeriod(plan: PlanData, idx: number, period: Period): { plan: PlanData; error?: string } {
  const n = plan.periods.length;
  if (!Number.isInteger(idx) || idx < 0 || idx >= n) return { plan };
  const durs = periodDurations(plan.periods);
  const periods = [...plan.periods];
  const oldStart = timeToMin(plan.periods[idx]!.time);
  const newStart = period.time ? timeToMin(fmt(period.time)) : oldStart;
  durs[idx] = Math.max(1, period.duration || DEFAULT_PERIOD_MIN);
  periods[idx] = { ...period };
  let start = getPlanStart(plan);
  if (newStart !== oldStart) {
    if (idx === 0) {
      start = minToTime(newStart);
    } else {
      const grown = durs[idx - 1]! + (newStart - oldStart);
      if (grown < 1) {
        return { plan, error: `Has to start after ${fmt(plan.periods[idx - 1]!.time)}, when the period before it begins` };
      }
      durs[idx - 1] = grown;
    }
  }
  return { plan: retime(plan, periods, durs, start) };
}

/** Every period's length in minutes, in list order (8 where it can't be derived). */
export function periodDurations(ps: Period[]): number[] {
  return ps.map((_, i) => periodDurationValue(ps, i));
}

/** Lay periods end to end from `start`, each keeping the length given for it. */
function retime(plan: PlanData, periods: Period[], durs: number[], start: HHMM): PlanData {
  let t = timeToMin(start);
  const out = periods.map((p, i) => {
    const q = { ...p, time: minToTime(t), duration: durs[i]! };
    t += durs[i]!;
    return q;
  });
  return { ...plan, startTime: fmt(start), periods: out };
}

/**
 * Put a new period in front of slot `at` (0..length). Practice still starts
 * when it did and every period keeps its length, so everything from the slot
 * on slides later by the new period's minutes. No period type is special-cased.
 */
export function insertPeriod(plan: PlanData, period: Period, at: number): PlanData {
  const start = getPlanStart(plan);
  const durs = periodDurations(plan.periods);
  const i = Math.max(0, Math.min(Math.floor(at), plan.periods.length));
  const d = period.duration || DEFAULT_PERIOD_MIN;
  const periods = [...plan.periods];
  periods.splice(i, 0, period);
  durs.splice(i, 0, d);
  return retime(plan, periods, durs, start);
}

/**
 * Move period `from` so it sits in front of slot `to` (0..length, counted in
 * the list as it is now). Lengths travel with their periods and the clock is
 * re-run from the practice start. Returns the same plan when nothing moves.
 */
export function movePeriod(plan: PlanData, from: number, to: number): PlanData {
  const n = plan.periods.length;
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || from >= n || to < 0 || to > n) return plan;
  if (to === from || to === from + 1) return plan;
  const start = getPlanStart(plan);
  const durs = periodDurations(plan.periods);
  const periods = [...plan.periods];
  const [p] = periods.splice(from, 1);
  const [d] = durs.splice(from, 1);
  const at = to > from ? to - 1 : to;
  periods.splice(at, 0, p!);
  durs.splice(at, 0, d!);
  return retime(plan, periods, durs, start);
}

/**
 * Take period `idx` out. Practice still starts when it did and every other
 * period keeps its length, so everything after it moves up — the mirror of
 * insertPeriod().
 */
export function removePeriod(plan: PlanData, idx: number): PlanData {
  if (!Number.isInteger(idx) || idx < 0 || idx >= plan.periods.length) return plan;
  const start = getPlanStart(plan);
  const durs = periodDurations(plan.periods).filter((_, i) => i !== idx);
  const periods = plan.periods.filter((_, i) => i !== idx);
  if (!periods.length) return { ...plan, startTime: start, periods };
  return retime(plan, periods, durs, start);
}

/** Suggested start time for a new period: after the last one. */
export function nextPeriodTime(plan: PlanData): HHMM {
  const last = plan.periods[plan.periods.length - 1];
  if (!last) return getPlanStart(plan);
  return minToTime(timeToMin(last.time) + (last.duration || DEFAULT_PERIOD_MIN));
}

/** End minute-of-day of the plan (last period start + its duration). */
export function planEndMin(plan: PlanData): number | null {
  const last = plan.periods[plan.periods.length - 1];
  if (!last) return null;
  return timeToMin(last.time) + (last.duration || DEFAULT_PERIOD_MIN);
}

/** True for the untouched default plan — nothing loaded, nothing built. */
export function planIsUntouched(plan: PlanData | null | undefined, loadedFromDb: boolean): boolean {
  if (!plan) return true;
  return !loadedFromDb && plan.name === 'New Practice' && plan.periods.length === 0;
}
